// Toast (STYLE 10): a short confirmation at the bottom of the screen after
// Save, Send, Remove, and after redirects that carry ?done=<key>. A failed save
// or delete that the screen does not show by itself appears as an error toast.
//   const toast = useToast(); toast("Saved");
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { WRITE_FAILED_EVENT, type WriteFailure } from "../api/queryClient";
import { useLocation, useNavigate } from "react-router";

type Show = (message: string) => void;
const ToastContext = createContext<Show>(() => {});

type Shown = { text: string; error: boolean };

/** Is the failure already on screen (a form's error box, a field error, "Not saved. ...")? */
function alreadyShown(failure: WriteFailure): boolean {
  const onScreen = (el: Element) => !el.closest(".toast") && !el.querySelector(".toast") && (el.textContent ?? "").includes(failure.message);
  if (Array.from(document.querySelectorAll('[role="alert"], [aria-live], .field__error')).some(onScreen)) return true;
  // A form that put a validation failure on its fields shows the fields' messages, not the summary sentence.
  if (failure.code === "VALIDATION_FAILED" && document.querySelector(".error-summary, .field__error")) return true;
  return false;
}

export function ToastProvider({ children, doneMessages = {} }: { children: ReactNode; doneMessages?: Record<string, string> }) {
  const [shown, setShown] = useState<Shown | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const display = useCallback((text: string, error: boolean) => {
    setShown({ text, error });
    if (timer.current) clearTimeout(timer.current);
    // An error stays longer: it has to be read, and it may say what to do.
    timer.current = setTimeout(() => setShown(null), error ? 8000 : 2600);
  }, []);
  const show = useCallback<Show>((m) => display(m, false), [display]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  useEffect(() => {
    // A failed save or delete (api/queryClient.ts). A form shows the failure itself (its "There is
    // a problem" box, role="alert"). Wait for that to render, and only toast when nothing on the
    // page already says it: one message, not two, and never none (Marius round 5, SY1).
    let wait: ReturnType<typeof setTimeout> | undefined;
    const onFailed = (e: Event) => {
      const failure = (e as CustomEvent<WriteFailure>).detail;
      if (!failure?.message) return;
      clearTimeout(wait);
      wait = setTimeout(() => {
        if (!alreadyShown(failure)) display(failure.message, true);
      }, 50);
    };
    window.addEventListener(WRITE_FAILED_EVENT, onFailed);
    return () => {
      clearTimeout(wait);
      window.removeEventListener(WRITE_FAILED_EVENT, onFailed);
    };
  }, [display]);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <DoneParam show={show} messages={doneMessages} />
      <div aria-live="polite" role="status">
        {shown && !shown.error ? <div className="toast">{shown.text}</div> : null}
      </div>
      {shown?.error ? (
        <div className="toast" role="alert">
          {shown.text}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

/** Reads ?done=<key>, shows its message once, then drops the parameter. */
function DoneParam({ show, messages }: { show: Show; messages: Record<string, string> }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Showing the toast re-renders the provider (and `messages` is a new object each time), so the
  // effect must run once per location, not once per render: otherwise it loops before the
  // parameter is gone ("Maximum update depth exceeded", the page freezes).
  const latest = useRef({ show, messages });
  latest.current = { show, messages };
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get("done");
    if (!key) return;
    latest.current.show(latest.current.messages[key] ?? DONE[key] ?? "Done");
    params.delete("done");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
  }, [location.key, location.pathname, location.search, location.hash, navigate]);
  return null;
}

/** Default wording for ?done= keys; areas may pass more to ToastProvider. */
const DONE: Record<string, string> = {
  saved: "Saved",
  sent: "Sent",
  removed: "Removed",
  created: "Created",
  uploaded: "Uploaded",
  invited: "Invitation sent",
};

export function useToast(): Show {
  return useContext(ToastContext);
}
